import { useEffect } from "react";
import { useSelector } from "react-redux";
import { BestPriceGuaranteeCard } from "@apac/components/ui";
import { useBooleanFlag } from "@apac/feature-flags";
import {
  DeparturePage as CoreDeparturePage,
  getFilteredAndSortedDepartureFlightList,
  getFlights,
} from "@b2bportal/core-flights";
import { useModuleBEM } from "@b2bportal/core-themes";
import { CoreFlightsComponent } from "@b2bportal/core-types";
import { trackEvent } from "@hopper-b2b/api";
import { useI18nContext } from "@hopper-b2b/i18n";
import { VIEWED_FLIGHT_LIST } from "@hopper-b2b/types";
import { usePointsBalance } from "@hsbc/rewards";
import { FeatureFlagNames } from "@hsbc/utilities";
import { getViewedForecastProperties } from "@hsbc/utilities";
import { PricePredictionCard } from "../PricePredictionCard";
import styles from "./styles.module.scss";

export const DeparturePage = (
  coreProps: React.ComponentProps<typeof CoreDeparturePage>
) => {
  const [, cn] = useModuleBEM(styles, CoreFlightsComponent.DeparturePage);
  const { isLoading } = useSelector(getFlights);
  const flightList = useSelector(getFilteredAndSortedDepartureFlightList);
  const viewedOutgoingListProperties = useSelector(getViewedForecastProperties);

  const enablePrediction = useBooleanFlag(
    FeatureFlagNames.ENABLED_PRICE_PREDICTION
  );
  const enableBPG = useBooleanFlag(FeatureFlagNames.BEST_PRICE_GUARANTEE);

  const enablePayWithPointsBanner = useBooleanFlag(
    FeatureFlagNames.SHOW_MERCHANDISING_PAY_WITH_POINTS_BANNER
  );
  const { t, formatFiatCurrency } = useI18nContext();
  const points = usePointsBalance();

  useEffect(() => {
    if (flightList && !isLoading && viewedOutgoingListProperties.origin) {
      trackEvent({
        eventName: VIEWED_FLIGHT_LIST,
        properties: {
          ...viewedOutgoingListProperties,
        },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewedOutgoingListProperties.origin, isLoading, flightList]);

  return (
    <CoreDeparturePage {...coreProps}>
      {!isLoading && enablePrediction && (
        <div className={cn("prediction")}>
          <PricePredictionCard />
        </div>
      )}
      {!isLoading && enableBPG && (
        <div className={cn("best-price-guarantee")}>
          <BestPriceGuaranteeCard />
        </div>
      )}
      {!isLoading && enablePayWithPointsBanner && (
        <div className={cn("pay-with-points")}>
          <div className={cn("pay-with-points-content")}>
            {t("payWithPoints.banner", {
              amount: formatFiatCurrency(points?.cashEquivalent, {
                minimumFractionDigits: 0,
                maximumFractionDigits: 0,
              }),
              points: points?.formattedPointsBalance,
            })}
          </div>
        </div>
      )}
    </CoreDeparturePage>
  );
};
